import { RotateCw, Compass, X, Navigation2, Download } from 'lucide-react';
import { toast } from 'sonner';
import { readCompass } from '../lib/bearing';
import { norm, dirCard, stampDir } from '../lib/photoDir';
import { rotateDataUrl, rotateFree } from '../lib/model';
import OverlayControls from './OverlayControls';
import { useState } from 'react';

// any angle: live preview, fine steps, then the image itself is rotated
function FreeRotate({ t, obj, patch }) {
  const [a, setA] = useState(0);
  const apply = async () => { patch(await rotateFree(obj.data, a)); setA(0); toast.success(t.phRotDone); };
  return (
    <div className="ph-free" data-testid="photo-free-rotate">
      <b>{t.phRotFree}: {a}°</b>
      <div className="ph-free-prev"><img src={obj.data} alt="" style={{ transform: `rotate(${a}deg)` }} data-testid="photo-free-preview" /></div>
      <input type="range" min="-180" max="180" step="1" value={a} data-testid="photo-free-slider" onChange={(e) => setA(+e.target.value)} />
      <div className="chip-group">
        {[-15, -1, 1, 15].map((k) => <button key={k} type="button" className="chip" data-testid={`photo-free-${k < 0 ? 'm' : 'p'}${Math.abs(k)}`} onClick={() => setA((v) => Math.max(-180, Math.min(180, v + k)))}>{k > 0 ? '+' : '−'}{Math.abs(k)}°</button>)}
        <button type="button" className="btn btn-dark btn-sm" disabled={!a} data-testid="photo-free-apply" onClick={apply}><RotateCw size={14} /> {t.phRotApply}</button>
      </div>
    </div>
  );
}

// capture direction of a map photo: arrow correction (±1/15/90°, slider, compass) + rotate the image itself
export default function PhotoDir({ t, obj, patch }) {
  const d = obj.dir;
  const set = (v) => patch({ dir: ((v % 360) + 360) % 360, dirSrc: 'manual' });
  const fromCompass = async () => { const v = await readCompass(); if (v == null) toast.error(t.phDirNoSensor); else patch({ dir: norm(v), dirSrc: 'compass' }); };
  const saveBig = async () => { const a = document.createElement('a'); a.href = await stampDir(obj, t); a.download = `${(obj.caption || 'Foto').replace(/[^\w-]+/g, '_').slice(0, 30)}_${d != null ? `${norm(d)}deg` : 'foto'}.jpg`; a.click(); };
  const rotImg = async () => { patch(await rotateDataUrl(obj.data)); toast.success(t.phRotDone); };
  return (
    <div className="ph-dir" data-testid="photo-dir-box">
      <div className="ph-dir-head">
        <span className={`ph-dir-rose ${d == null ? 'off' : ''}`}><Navigation2 size={18} style={{ transform: `rotate(${d ?? 0}deg)` }} data-testid="photo-dir-rose-arrow" /></span>
        <b>{t.phDir}</b>
        <span className="muted" data-testid="photo-dir-value">{d == null ? t.phDirNone : `${norm(d)}° ${dirCard(t, d)} · ${t['phDirSrc_' + (obj.dirSrc || 'manual')]}`}</span>
      </div>
      {d != null && (
        <>
          <div className="chip-group">
            {[-15, -1, 1, 15, 90].map((k) => <button key={k} type="button" className="chip" data-testid={`photo-dir-adj-${k < 0 ? 'm' : 'p'}${Math.abs(k)}`} onClick={() => set(d + k)}>{k > 0 ? '+' : '−'}{Math.abs(k)}°</button>)}
          </div>
          <input type="range" min="0" max="359.5" step="0.5" value={d} data-testid="photo-dir-slider" onChange={(e) => set(+e.target.value)} />
        </>
      )}
      <div className="row">
        {d == null && <button type="button" className="btn btn-outline btn-sm" data-testid="photo-dir-set-btn" onClick={() => set(0)}><Navigation2 size={14} /> {t.phDirSet}</button>}
        <button type="button" className="btn btn-outline btn-sm" data-testid="photo-dir-compass-btn" onClick={fromCompass}><Compass size={14} /> {t.phDirCompass}</button>
        {d != null && <button type="button" className="btn btn-outline btn-sm" data-testid="photo-dir-clear-btn" onClick={() => patch({ dir: null, dirSrc: null })}><X size={14} /> {t.phDirClear}</button>}
        {obj.kind !== 'video' && obj.data && <button type="button" className="btn btn-primary btn-sm" data-testid="photo-save-dir-btn" onClick={saveBig}><Download size={14} /> {t.phSaveDir}</button>}
        {obj.kind !== 'video' && obj.data && <button type="button" className="btn btn-dark btn-sm" data-testid="photo-rotate-img-btn" onClick={rotImg}><RotateCw size={14} /> {t.phRotImg}</button>}
      </div>
      {obj.kind !== 'video' && obj.data && <FreeRotate t={t} obj={obj} patch={patch} />}
      {obj.kind !== 'video' && obj.data && (
        <div className="seg-group ph-ov-mode" data-testid="photo-overlay-toggle">
          <button type="button" className={`chip seg ${!obj.ov ? 'on' : ''}`} data-testid="photo-show-small-btn" onClick={() => patch({ ov: false })}>◉ {t.phOvSmall}</button>
          <button type="button" className={`chip seg ${obj.ov ? 'on' : ''}`} data-testid="photo-show-big-btn" onClick={() => patch({ ov: true })}>🖼 {t.phOvBig}</button>
        </div>
      )}
      {obj.ov && <label className="ph-ov-size" data-testid="photo-overlay-size">{t.phOvSize} <input type="range" min="50" max="250" step="10" data-testid="photo-overlay-size-input" value={obj.ovS || 100} onChange={(e) => patch({ ovS: +e.target.value })} /> {obj.ovS || 100} %</label>}
    </div>
  );
}
